import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import {
  AdditionalWorkIcon,
  Button,
  IconButton,
  Paper,
  Spinner,
  StatusDot,
  Typography,
  useClientOptions,
  useDialogs
} from '@paintscout/ui'
import type { QuoteDocument } from 'paintscout'
import { makeStyles } from '@material-ui/core'
import React, { useCallback } from 'react'
import CropFreeIcon from '@material-ui/icons/CropFree'
import { getItems } from '@paintscout/util/builder/quote'
import AddIcon from '@material-ui/icons/Add'
import { useQuoteQuery } from '@paintscout/api'
import type { Quote } from '@paintscout/api'
import { Sentry } from 'src/util/appSentry'
import { goToQuote } from 'src/util/routing'
import WorkOrderViewDialog from '../WorkOrderViewDialog'

const useStyles = makeStyles<Theme, DealWorkOrderCardProps>(
  (theme) => ({
    root: {
      [theme.breakpoints.down('sm')]: {
        border: 'none'
      }
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1)
    },
    titleContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    newButton: {
      marginRight: -12
    },
    wordContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      borderLeft: `2px solid ${theme.palette.grey[300]}`,
      paddingLeft: theme.spacing(1.75),
      marginLeft: theme.spacing(0.5)
    },
    additionalWorkContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0)
    },
    additionalWorkText: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      '&:hover': {
        cursor: 'pointer'
      }
    },
    additionalWorkCount: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      paddingTop: 0,
      '&:hover': {
        cursor: 'pointer'
      }
    },
    statusContainer: {
      display: 'flex',
      marginLeft: theme.spacing(1)
    },
    acceptedText: {
      color: theme.palette.success.main,
      marginLeft: `-${theme.spacing(0.5)}px`
    },
    pendingText: {
      color: theme.palette.warning.main,
      marginLeft: `-${theme.spacing(0.5)}px`
    },
    noInvoiceText: {
      display: 'flex',
      justifyContent: 'center',
      padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`
    }
  }),
  { name: 'DealWorkOrderCard' }
)

export interface DealWorkOrderCardProps {
  classes?: StyleClasses<typeof useStyles>
  relatedDoc: Partial<Quote>
  loading?: boolean
  hideTitle?: boolean
}

function DealWorkOrderCard(props: DealWorkOrderCardProps) {
  const { relatedDoc, hideTitle = false } = props
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const [fullDoc, setFullDoc] = React.useState<QuoteDocument | null>(null)
  const { openDialog, dismissDialog } = useDialogs()
  const hours = Math.round((relatedDoc?.totalHours || 0) * 100) / 100
  const { loading, error } = useQuoteQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id: relatedDoc?._id },
    skip: !relatedDoc,
    onCompleted: (data) => {
      setFullDoc(data.quote as QuoteDocument)
    },
    onError: useCallback((e) => {
      console.log('Error fetching quote in DealWorkOrderCard:', e)
      Sentry.captureException(e, { tags: { quoteId: relatedDoc?._id } })
    }, [])
  })

  const additionalWork = React.useMemo(() => {
    if (!fullDoc) {
      return { total: 0, accepted: 0, pending: 0 }
    }
    const pendingItems = getItems({
      quote: fullDoc,
      section: 'pending',
      view: 'area',
      consumer: 'customer',
      options
    })
    const acceptedItems = getItems({
      quote: fullDoc,
      section: 'additional',
      view: 'area',
      consumer: 'customer',
      options
    })
    return {
      total: pendingItems.length + acceptedItems.length,
      accepted: acceptedItems.length,
      pending: pendingItems.length
    }
  }, [fullDoc])

  function handleViewWorkOrder(scrollToBottom?: boolean) {
    openDialog(WorkOrderViewDialog, {
      quote: fullDoc as QuoteDocument,
      onCancel: dismissDialog,
      scrollToBottom
    })
  }
  return (
    <Paper className={classes.root}>
      <div className={classes.header} style={hideTitle && !relatedDoc ? { display: 'none' } : undefined}>
        <div className={classes.titleContainer}>
          {!hideTitle && <Typography variant="h3">{'Work Order'}</Typography>}
          {relatedDoc && (
            <div
              className={classes.wordContainer}
              style={hideTitle ? { borderLeft: 'none', paddingLeft: 0, marginLeft: 0 } : undefined}
            >
              <Typography variant="h3">{hours.toString()}</Typography>
              <Typography variant="h4">hours</Typography>
            </div>
          )}
        </div>
        <div>
          {relatedDoc && (
            <IconButton
              color="inherit"
              aria-label="View Work Order"
              disabled={!fullDoc}
              onClick={() => handleViewWorkOrder()}
            >
              <CropFreeIcon />
            </IconButton>
          )}
        </div>
      </div>

      {relatedDoc ? (
        <div className={classes.additionalWorkContainer}>
          <div className={classes.additionalWorkText} onClick={() => handleViewWorkOrder(additionalWork.accepted > 0)}>
            <AdditionalWorkIcon style={{ fontSize: '1.1rem' }} />
            <Typography variant="h4" weight={'normal'}>
              {'Additional Work'}
            </Typography>
          </div>
          <Button
            onClick={() => goToQuote({ id: relatedDoc?._id, invoice: true })}
            className={classes.newButton}
            variant="hoverUnderline"
            color="primary"
            startIcon={<AddIcon />}
          >
            New
          </Button>
        </div>
      ) : (
        <div className={classes.noInvoiceText}>
          <Typography variant="subtitle2" color="textSecondary">
            {'Deal requires an invoice to display the work order.'}
          </Typography>
        </div>
      )}
      {relatedDoc && (
        <div className={classes.additionalWorkCount} onClick={() => handleViewWorkOrder(additionalWork.accepted > 0)}>
          {additionalWork.total > 0 ? (
            <div className={classes.additionalWorkText}>
              <Typography variant="subtitle1" color="textSecondary">
                {additionalWork.total} items
              </Typography>
              <div className={classes.statusContainer}>
                <StatusDot status="accepted" dense />
                <Typography variant="subtitle1" className={classes.acceptedText}>
                  {additionalWork.accepted} accepted
                </Typography>
              </div>
              <div className={classes.statusContainer}>
                <StatusDot status="pending" dense />
                <Typography variant="subtitle1" className={classes.pendingText}>
                  {additionalWork.pending} pending
                </Typography>
              </div>
            </div>
          ) : loading ? (
            <Spinner />
          ) : error ? (
            <Typography variant="subtitle1" color="error">
              Error loading items
            </Typography>
          ) : (
            <Typography variant="subtitle1" color="textSecondary">
              No items
            </Typography>
          )}
        </div>
      )}
    </Paper>
  )
}

export default DealWorkOrderCard
